/**
 * Header Editing Plugin
 *
 * Handles inline editing for header nodes with per-keystroke behavior
 * driven by the node's autofit settings (from the Auto-Fit menu).
 *
 * Three modes:
 * - Auto-fit (default: autoFitWidth/Height on): Fixed font size (48px),
 *   node resizes per keystroke on enabled axes. Top-left stays anchored.
 * - Auto-size-text (autoSizeText on): Font scales to fill the box per
 *   keystroke, node size stays fixed.
 * - Neither: No per-keystroke adjustment.
 */

import type { EditingPlugin, EditingPluginContext } from "../editing";
import { createContentEditablePlugin } from "./content-editable";
import {
  computeHeaderFontSize,
  AUTO_HEADER_FONT_SIZE,
} from "@rool/renderer";
import { getAutoFitSettings } from "../autofit-settings";

/** Padding the .header-text element applies (CSS: padding: 12px 16px) */
const PAD_H = 16; // horizontal each side
const PAD_V = 12; // vertical each side

/**
 * Measure the actual text content dimensions inside a contentEditable element
 * using a Range. This gives us the text bounding box independent of the
 * element's own width/height (which scrollWidth/scrollHeight are capped to
 * when the content fits inside a border-box element).
 */
function measureTextContent(element: HTMLElement): { width: number; height: number } {
  // If the element is empty, there's nothing to measure
  if (!element.firstChild) {
    return { width: 0, height: 0 };
  }

  const range = document.createRange();
  range.selectNodeContents(element);
  const rect = range.getBoundingClientRect();

  // The range rect is in screen coordinates; we need to undo the canvas
  // zoom transform to get world-pixel dimensions.
  // Walk up to find the transform-layer's scale.
  let zoom = 1;
  let el: HTMLElement | null = element;
  while (el) {
    const transform = el.style.transform || getComputedStyle(el).transform;
    if (transform && transform !== 'none') {
      const match = transform.match(/scale\(([^)]+)\)/);
      if (match) {
        zoom = parseFloat(match[1]);
        break;
      }
    }
    el = el.parentElement;
  }

  return {
    width: rect.width / zoom,
    height: rect.height / zoom,
  };
}

/**
 * Create a header editing plugin with per-keystroke resize/font-fit.
 */
export function createHeaderEditingPlugin(): EditingPlugin {
  return createContentEditablePlugin(["header"], ".header-text", {
    onInput: (element: HTMLElement, _nodeId: string, ctx: EditingPluginContext) => {
      const nodeState = ctx.getNodeState();
      if (!nodeState) return;

      const settings = getAutoFitSettings({
        type: 'header',
        data: nodeState.data as Record<string, unknown>,
      });
      const content = element.innerText || "";

      if (settings.autoSizeText) {
        // Auto-size-text mode: scale font to fill box, don't resize
        const fontSize = computeHeaderFontSize(
          nodeState.size.width,
          nodeState.size.height,
          content,
        );
        element.style.fontSize = fontSize + "px";
        element.style.whiteSpace = "pre-wrap";
        element.style.wordBreak = "break-word";
      } else if (settings.autoFitWidth || settings.autoFitHeight) {
        // Auto-fit mode: fixed font size, resize node on enabled axes
        element.style.fontSize = AUTO_HEADER_FONT_SIZE + "px";
        element.style.whiteSpace = settings.wordWrap ? "pre-wrap" : "nowrap";
        element.style.wordBreak = settings.wordWrap ? "break-word" : "normal";

        // Measure actual text content via Range (not scrollWidth which
        // includes padding and caps at clientWidth in border-box elements)
        const textSize = measureTextContent(element);

        // Only resize enabled axes; preserve the other.
        // Ceil the text dimensions to produce integer sizes that match the
        // content measurer (which also uses Math.ceil). Without this, the
        // fractional per-keystroke width can differ from the integer width
        // that autoFitNode computes after editing, causing the node to
        // shrink and truncate text.
        const newWidth = settings.autoFitWidth
          ? Math.max(Math.ceil(textSize.width) + PAD_H * 2, 60)
          : nodeState.size.width;
        const newHeight = settings.autoFitHeight
          ? Math.max(Math.ceil(textSize.height) + PAD_V * 2, 40)
          : nodeState.size.height;

        // Only resize if change is significant (>2px threshold)
        if (
          Math.abs(newWidth - nodeState.size.width) >= 2 ||
          Math.abs(newHeight - nodeState.size.height) >= 2
        ) {
          // Anchor top-left: compute new center from top-left + new size
          const topLeftX = nodeState.position.x;
          const topLeftY = nodeState.position.y;
          ctx.resizeNode({
            cx: topLeftX + newWidth / 2,
            cy: topLeftY + newHeight / 2,
            width: newWidth,
            height: newHeight,
          });
          ctx.render();
        }
      }
      // else: no autoSizeText and no autoFit — no per-keystroke adjustment
    },
  });
}
